/**
 * Safely convert (possibly imported) HTML into readable plain text for summaries.
 *
 * Uses the browser DOM parser and `textContent` — it never inserts HTML into the page
 * (no dangerouslySetInnerHTML). Script/style/head/title/noscript/template nodes are
 * removed so their contents (JS, CSS) never leak into the summary. Block-level elements
 * and <br> are turned into whitespace boundaries in the inert parsed document BEFORE
 * reading text, so "<p>First</p><p>Second</p>" becomes "First Second" (not "FirstSecond").
 * HTML entities are decoded by `textContent`. Whitespace is collapsed and the result
 * optionally truncated.
 *
 * For full rich-text rendering use `sanitizeHtml` instead — this is for short summaries.
 */
const BLOCK_SELECTOR =
  'p,div,li,ul,ol,table,thead,tbody,tfoot,tr,h1,h2,h3,h4,h5,h6,blockquote,' +
  'section,article,header,footer,pre,hr,figure,figcaption';

export function htmlToText(html, { maxLength = 0 } = {}) {
  if (!html || typeof html !== 'string') return '';

  let text;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc
      .querySelectorAll('script, style, head, title, noscript, template')
      .forEach((el) => el.remove());

    // Preserve line/word boundaries before flattening to text.
    doc.querySelectorAll('br').forEach((br) => br.replaceWith(doc.createTextNode('\n')));
    doc.querySelectorAll(BLOCK_SELECTOR).forEach((el) => {
      el.appendChild(doc.createTextNode('\n'));
    });

    text = doc.body ? doc.body.textContent || '' : '';
  } catch {
    // Extremely defensive fallback; DOMParser is available in all supported browsers.
    text = html.replace(/<[^>]*>/g, ' ');
  }

  text = text.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();

  if (maxLength > 0 && text.length > maxLength) {
    text = text.slice(0, maxLength).trimEnd() + '…';
  }
  return text;
}
